import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Table from "../../../components/Table";
import { usePersistStore, useStore } from "../../../store";
import { Utils } from "../../../utils";
import AdSortingDropdown from "../../../components/AdSortingDropdown";
import TableSearchInput from "../../../components/TableSearchInput";
import { useMyOpenListings } from "../../../hooks/queries";

function OpenForBids() {
  const [sorting, setSorting] = useState("");
  const [keyword, setKeyword] = useState("");
  // Search applies on Enter (not per keystroke), preserving prior behavior.
  const [appliedKeyword, setAppliedKeyword] = useState("");
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const { token } = usePersistStore((s: any) => s);
  const { setErrorMessage } = useStore((s: any) => s);

  const { data, isLoading, error } = useMyOpenListings(token, {
    keyword: appliedKeyword,
    page,
    limit: rowsPerPage,
    sorting,
  });
  const list = data?.ads || [];
  const count = data?.count || 0;
  const isDone = !isLoading;

  useEffect(() => {
    if (error) setErrorMessage((error as Error).message);
  }, [error, setErrorMessage]);

  const columns = useMemo(
    () => [
      {
        title: "Code",
        thAlign: "left",
        tdAlign: "left",
        formatter: (row: any) =>
          row?.code ? (
            <span className="refCode">{row.code}</span>
          ) : (
            <span style={{ color: "var(--text-muted)" }}>—</span>
          ),
      },
      {
        title: "Title",
        thAlign: "left",
        tdAlign: "left",
        formatter: (row: any) => (
          <Link to={`/details/${row?._id}`} style={{ color: "var(--main-color)", textDecoration: "none" }}>
            {row?.title}
          </Link>
        ),
      },
      {
        title: "Opening Price",
        thAlign: "right",
        tdAlign: "right",
        formatter: (row: any) => Utils.formatToCurrency(row?.price),
      },
      {
        title: "Bids",
        thAlign: "right",
        tdAlign: "right",
        formatter: (row: any) => Utils.formatToNumber(row?.bids || 0),
      },
      {
        title: "Views",
        thAlign: "right",
        tdAlign: "right",
        formatter: (row: any) => Utils.formatToNumber(row?.views || 0),
      },
      {
        title: "Bidding Starts",
        thAlign: "center",
        tdAlign: "center",
        formatter: (row: any) => Utils.formatDateTime(row?.biddingStarts),
      },
      {
        title: "Closes at",
        thAlign: "center",
        tdAlign: "center",
        formatter: (row: any) => Utils.formatDateTime(row?.biddingEnds),
      },
      {
        title: "Time Left",
        thAlign: "center",
        tdAlign: "center",
        formatter: (row: any) => Utils.formatFromNow(row?.biddingEnds),
      },
    ],
    []
  );

  const renderMobileCard = useCallback(
    (row: any) => (
      <div className="bidsCard">
        <div className="bidsCard__top">
          <div>
            <Link to={`/details/${row?._id}`} className="bidsCard__title">
              {row?.title}
            </Link>
            {row?.code && (
              <div className="refCode refCode--sub">{row.code}</div>
            )}
          </div>
          <div className="bidsCard__status">
            <span className="text-primary fw-semibold">
              {Utils.formatFromNow(row?.biddingEnds)}
            </span>
          </div>
        </div>
        <div className="bidsCard__grid">
          <div className="bidsCard__stat">
            <span className="bidsCard__statLabel">Opening</span>
            <span className="bidsCard__statValue">
              {Utils.formatToCurrency(row?.price)}
            </span>
          </div>
          <div className="bidsCard__stat">
            <span className="bidsCard__statLabel">Bids</span>
            <span className="bidsCard__statValue">
              {Utils.formatToNumber(row?.bids || 0)}
            </span>
          </div>
          <div className="bidsCard__stat">
            <span className="bidsCard__statLabel">Views</span>
            <span className="bidsCard__statValue">
              {Utils.formatToNumber(row?.views || 0)}
            </span>
          </div>
        </div>
        <div className="bidsCard__meta">
          <div className="bidsCard__metaRow">
            <i className="bi bi-calendar-event" aria-hidden="true"></i>
            <span>Starts {Utils.formatDateTime(row?.biddingStarts)}</span>
          </div>
          <div className="bidsCard__metaRow">
            <i className="bi bi-hourglass-split" aria-hidden="true"></i>
            <span>Closes {Utils.formatDateTime(row?.biddingEnds)}</span>
          </div>
        </div>
      </div>
    ),
    []
  );

  return (
    <div className="bg-pink p-4">
      <div className="row">
        <div className="col-12">
          <div className="chartCard py-4">
            <div className="tableCardHeader px-4">
              <div className="tableCardHeader__titleBlock">
                <h2 className="tableCardHeader__title m-0">Open for bids</h2>
                <p className="tableCardHeader__subtitle m-0">
                  {isDone
                    ? `${count} ${count === 1 ? "listing" : "listings"} open for bidding`
                    : "Loading your live listings…"}
                </p>
              </div>
              <div className="tableCardHeader__actions customTitle">
                <AdSortingDropdown value={sorting} onChange={setSorting} />
                <TableSearchInput
                  value={keyword}
                  onChange={setKeyword}
                  onEnter={() => setAppliedKeyword(keyword)}
                />
              </div>
            </div>
            <Table
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              page={page}
              setPage={setPage}
              columns={columns}
              rows={list}
              isDone={isDone}
              count={count}
              mobileCard={renderMobileCard}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default OpenForBids;
